import jsonPointer from 'json-pointer';
import set from 'set-value';
import isEqual from 'react-fast-compare';
import isUndefined from 'lodash/isUndefined';
import AbstractControl from './AbstractControl';
import toDataPath from '../utils/toDataPath';
import getPointer from '../utils/getPointer';
import getDict from '../utils/getDict';
import combineProps from '../utils/combineProps';
import isObjectLike from '../utils/isObjectLike';

export default class FormGroupControl extends AbstractControl {
  constructor(props) {
    super(props);

    this.initSubscriptions(['change', 'invalid', 'schema', 'valid', 'validateFields']);
  }

  withContext(context, $ref, customSchema) {
    super.withContext(context);

    if ($ref !== this.$ref) {
      this.$ref = $ref;
      this.dataPath = toDataPath($ref);
    }

    this.customSchema = customSchema;

    if (context != null) {
      this.group = this.customSchema || this.getRefFromSchema();

      const { schema, initialValues, defaultValues, keepState, ...UIProps } = combineProps(
        this.props,
        this.group['ui:props']
      );

      this.userSchema = schema;
      this.keepState = keepState;
      this.fields = this.computeFields(this.group);
      this.values = this.computeValues();
      this.defaultValues = this.computeDefaultValues(defaultValues);
      this.initialValues = this.computeInitialValues(initialValues);
      this.errors = this.computeErrors();
      this.touched = this.computeTouched();
      this.title = this.props?.title ?? this.i18n(this.dataPath, 'title', this.group.title);
      this.description =
        this.props?.description ?? this.i18n(this.dataPath, 'description', this.group.description);
      this.type = this.group.type ?? (isObjectLike(this.group) ? 'object' : undefined);
      this.UIProps = UIProps;
      this.isValid = Object.keys(this.errors).length === 0;
      this.isPristine = !this.context.$submitted && isEqual(this.initialValues, this.values);
    }

    return this;
  }

  subscribeContext = () => {
    const { register, subscribe } = this.context;

    register(this.dataPath, {
      $ref: this.$ref,
      defaultValue: this.defaultValues,
      formFieldControl: this,
      initialValue: this.initialValues,
      keepState: this.keepState,
      schema: this.userSchema,
    });

    // this.context.updateUserSchema(this.$ref, this.userSchema);

    subscribe('aftervalidate', this.handleValidateFieldsEvent);
    subscribe('change', this.handleChangeEvent);
    subscribe('error', this.handleErrorChangeEvent);
    subscribe('schema', this.handleSchemaEvent);
  };

  unsubscribeContext = () => {
    const { unregister, unsubscribe } = this.context;

    unregister(this.dataPath, {
      $ref: this.$ref,
      keepState: this.keepState,
    });

    unsubscribe('aftervalidate', this.handleValidateFieldsEvent);
    unsubscribe('change', this.handleChangeEvent);
    unsubscribe('error', this.handleErrorChangeEvent);
    unsubscribe('schema', this.handleSchemaEvent);
  };

  updateUserSchema = (schema) => {
    this.context.updateUserSchema(this.$ref, schema);
  };

  updateDefaultValues = (defaultValues) => {
    this.context.updateDefaultValue(this.dataPath, defaultValues);
  };

  updateInitialValues = (initialValues) => {
    this.context.updateInitialValue(this.dataPath, initialValues);
  };

  handleChangeEvent = (changedValues) => {
    if (
      Object.keys(changedValues).some((key) =>
        Object.keys(this.fields)
          .map(($ref) => toDataPath($ref))
          .includes(key)
      )
    ) {
      this.trigger('change', this.ownContext);
    }
  };

  handleErrorChangeEvent = (changedErrors) => {
    if (
      Object.keys(changedErrors).some((key) =>
        Object.keys(this.fields)
          .map(($ref) => toDataPath($ref))
          .includes(key)
      )
    ) {
      const { errors } = this.properties;
      let errorHandler = 'valid';

      if (errors) {
        errorHandler = 'invalid';
      }

      this.trigger(errorHandler, this.ownContext);
    }
  };

  handleSchemaEvent = (changedSchemaMap) => {
    let compareFields = [this.$ref];

    if (this.customSchema) {
      compareFields = Object.values(getDict(this.customSchema)).filter((dataPath) => {
        return typeof dataPath === 'string' && dataPath.charAt(0) === '/';
      });
    }

    if (Object.keys(changedSchemaMap).some((key) => compareFields.includes(key))) {
      this.trigger('schema', this.ownContext);
    }
  };

  computeValues() {
    if (this.dataPath != null) {
      return getPointer(this.context.values, this.dataPath, {});
    }

    const values = {};

    if (this.customSchema) {
      for (const $ref of Object.keys(this.fields)) {
        const dataPath = toDataPath($ref);

        set(
          values,
          jsonPointer.parse(dataPath).join('.'),
          getPointer(this.context.values, dataPath)
        );
      }
    }

    return values;
  }

  computeDefaultValues(userDefaultValues) {
    if (!isUndefined(userDefaultValues)) {
      return userDefaultValues;
    }

    if (this.dataPath) {
      return getPointer(this.context.defaultValues, this.dataPath);
    }

    const defaultValues = {};

    if (this.customSchema) {
      for (const $ref of Object.keys(this.fields)) {
        const dataPath = toDataPath($ref);

        set(
          defaultValues,
          dataPath.replace('/', '.'),
          (this.context.defaultValues ?? {})[dataPath]
        );
      }
    }

    return defaultValues;
  }

  computeInitialValues(userInitialValues) {
    if (!isUndefined(userInitialValues)) {
      return userInitialValues;
    }

    if (this.dataPath) {
      return getPointer(this.context.initialValues, this.dataPath);
    }

    const initialValues = {};

    if (this.customSchema) {
      for (const $ref of Object.keys(this.fields)) {
        const dataPath = toDataPath($ref);

        set(
          initialValues,
          dataPath.replace('/', '.'),
          (this.context.initialValues ?? {})[dataPath]
        );
      }
    }

    return initialValues;
  }

  computeErrors() {
    if (this.dataPath) {
      return getPointer(this.context.errorsDict, this.dataPath, {});
    }

    const errors = {};

    if (this.customSchema) {
      for (const $ref of Object.keys(this.fields)) {
        const dataPath = toDataPath($ref);

        set(errors, dataPath.replace('/', '.'), this.context.errorsDict[dataPath]);
      }
    }

    return errors;
  }

  computeTouched() {
    const touched = [];
    const touchedFields = Array.from(this.context.touched.values());

    if (this.dataPath) {
      return touchedFields.filter((touchedDataPath) => touchedDataPath.indexOf(this.dataPath) > -1);
    }

    if (this.customSchema) {
      for (const $ref of Object.keys(this.fields)) {
        const dataPath = toDataPath($ref);
        const touchedField = touchedFields.find((touchedDataPath) => touchedDataPath === dataPath);

        if (touchedField != null) {
          touched.push(touchedField);
        }
      }
    }

    return touched;
  }

  getProps = () => ({
    ...this.UIProps,
    title: this.title,
    description: this.description,
  });

  get properties() {
    const isTouched = this.touched.length > 0;

    return {
      $dirty: !this.isPristine,
      $invalid: !this.isValid,
      $pristine: this.isPristine,
      $touched: isTouched,
      $untouched: !isTouched,
      $valid: this.isValid,
      defaultValues: this.defaultValues,
      fields: this.fields,
      initialValues: this.initialValues,
      errors: this.errors,
      values: this.values,
    };
  }
}
